import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { STORAGE_KEYS } from 'auth/storageKeys';
import { propertyService } from 'api/services/propertyService';
import { outputProfileService, base64ToFile } from 'api/services/outputProfileService';
import { openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { FONT_SCALES, type OutputDocumentType, type OutputProfile } from 'api/types/outputProfile.types';

/**
 * ============================================================================
 * PRINT LAYOUTS — document output profiles (ADR-0001, slice GRC-2)
 * ============================================================================
 * How a property prints a document: the built-in layout, the logo, the name and
 * header lines, terms, footer, font size, and which optional boxes show (and what
 * they are called). Layouts are code, so mandatory content — a GRC's guest, stay,
 * nationality / ID and foreign-national block — always prints. Empty text prints
 * the built-in default from the property master. The registration card (GRC) is
 * the first document here; invoices and bills follow when they are next changed.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const DOCUMENT: OutputDocumentType = 'GRC';

interface FormState {
  layoutCode: string;
  displayName: string;
  headerLines: string;
  termsText: string;
  footerText: string;
  fontScale: number;
  hidden: string[];
  labels: Record<string, string>;
}

const toForm = (p: OutputProfile): FormState => ({
  layoutCode: p.layoutCode,
  displayName: p.displayName ?? '',
  headerLines: p.headerLines ?? '',
  termsText: p.termsText ?? '',
  footerText: p.footerText ?? '',
  fontScale: p.fontScale,
  hidden: p.hidden,
  labels: p.labels,
});

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const PrintLayoutsScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const canEdit = allowedActions.includes('edit');

  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [profile, setProfile] = useState<OutputProfile | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const appLogo = useMemo(() => {
    try { return localStorage.getItem(STORAGE_KEYS.TENANT_LOGO); } catch { return null; }
  }, []);

  const applyProfile = useCallback(async (p: OutputProfile) => {
    setProfile(p);
    setForm(toForm(p));
    setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    if (p.hasLogo) {
      try {
        const blob = await outputProfileService.logo(p.propertyId, DOCUMENT);
        setLogoUrl(URL.createObjectURL(blob));
      } catch { /* the preview simply shows no image */ }
    }
  }, []);

  const load = useCallback(async () => {
    if (!propertyId) { setProfile(null); setForm(null); return; }
    setLoading(true);
    try {
      await applyProfile(await outputProfileService.get(propertyId, DOCUMENT));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The print layout did not load.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, applyProfile]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  const dirty = !!profile && !!form && JSON.stringify(toForm(profile)) !== JSON.stringify(form);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => (f ? { ...f, [key]: value } : f));

  const run = useCallback(async (work: () => Promise<OutputProfile>, done: string) => {
    setBusy(true);
    try {
      await applyProfile(await work());
      notificationService.toast.success(done);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  }, [applyProfile]);

  const save = () => form && profile && run(() => outputProfileService.save(propertyId, DOCUMENT, {
    layoutCode: form.layoutCode,
    displayName: form.displayName.trim() || null,
    headerLines: form.headerLines.trim() || null,
    termsText: form.termsText.trim() || null,
    footerText: form.footerText.trim() || null,
    fontScale: form.fontScale,
    hidden: form.hidden,
    labels: form.labels,
    rowVersion: profile.rowVersion,
  }), 'Print layout saved.');

  const applyAppLogo = () => appLogo && run(() => outputProfileService.uploadLogo(propertyId, DOCUMENT, base64ToFile(appLogo, 'application-logo')),
    'The application logo is now on the registration card.');
  const uploadLogo = (file: File) => run(() => outputProfileService.uploadLogo(propertyId, DOCUMENT, file), 'Logo saved.');
  const removeLogo = () => run(() => outputProfileService.removeLogo(propertyId, DOCUMENT), 'Logo removed. The card prints a text header.');

  // ---- copy from another property --------------------------------------------------------
  const [copyFrom, setCopyFrom] = useState('');
  const copy = () => copyFrom && run(() => outputProfileService.copyFrom(propertyId, DOCUMENT, copyFrom),
    'Layout copied. The name and header lines stay this property’s own.');

  const toggleField = (code: string, show: boolean) =>
    set('hidden', show ? (form?.hidden ?? []).filter((h) => h !== code) : [...(form?.hidden ?? []), code]);
  const setLabel = (code: string, value: string) => set('labels', { ...(form?.labels ?? {}), [code]: value });

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(() => { void load(); })];
  const disabled = !canEdit || busy;

  return (
    <EnterpriseScreenShell
      title="Print Layouts"
      description="How each property prints its documents: logo, header, terms and optional boxes. Starting with the guest registration card."
      icon={<IconSystem.print size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ minWidth: 280 }}>
          <SyutSelect
            label="Property"
            isAsync
            loadOptions={loadPropertyOptions}
            defaultOptions
            cacheOptions={false}
            value={propertyId}
            onChange={(v) => setPropertyId((v as string) || '')}
            placeholder="Select property..."
            size="sm"
            menuPortalTarget={document.body}
          />
        </div>
        <span className="badge badge-info" style={{ marginBottom: '0.4rem' }}>Guest registration card</span>
        {profile && (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>
            {profile.isSaved
              ? `Saved${profile.updatedByName ? ` by ${profile.updatedByName}` : ''}${profile.updatedAt ? `, ${fmtDate(profile.updatedAt)}` : ''}`
              : 'Not saved yet: printing with the built-in defaults'}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', marginBottom: '0.2rem' }}>
          {profile && (
            <button className="btn btn-secondary btn-sm" disabled={busy || dirty}
              title={dirty ? 'Save first: the preview prints the saved layout' : 'The card with a sample guest, as saved'}
              onClick={() => { void openPdf(() => outputProfileService.preview(propertyId, DOCUMENT), 'The preview did not print.'); }}>
              <IconSystem.eye size={14} /> Preview
            </button>
          )}
          {profile && canEdit && (
            <button className="btn btn-primary btn-sm" disabled={busy || !dirty} onClick={() => { void save(); }}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {loading && !profile && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
      {!propertyId && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Select a property</div>}

      {profile && form && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem', alignItems: 'start' }}>
          {/* ---- header ---- */}
          <section className="card" style={{ padding: '1rem', minWidth: 0 }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Header</h3>

            <label className="form-label">Logo</label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <div style={{
                width: 96, height: 64, border: '1px dashed var(--border-color)', borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)',
              }}>
                {logoUrl
                  ? <img src={logoUrl} alt="Logo on the card" style={{ maxWidth: 88, maxHeight: 56, objectFit: 'contain' }} />
                  : <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>No logo</span>}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {appLogo && (
                    <button className="btn btn-secondary btn-sm" disabled={disabled} onClick={() => { void applyAppLogo(); }}
                      title="The logo shown in this application's header">
                      Use the application logo
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" disabled={disabled} onClick={() => fileInput.current?.click()}>Upload…</button>
                  {profile.hasLogo && <button className="btn btn-secondary btn-sm" disabled={disabled} onClick={() => { void removeLogo(); }}>Remove</button>}
                  <input ref={fileInput} id="print-logo-file" type="file" accept="image/png,image/jpeg,image/webp" hidden
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadLogo(f); }} />
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '-0.4rem 0 0.75rem' }}>
              PNG, JPEG or WebP up to 2 MB. The application logo can be small; upload the hotel&apos;s original file for a sharp print.
            </div>

            <label className="form-label" htmlFor="pl-name">Name</label>
            <input id="pl-name" className="form-input" value={form.displayName} maxLength={200} disabled={disabled}
              placeholder={profile.defaultDisplayName} onChange={(e) => set('displayName', e.target.value)} />

            <label className="form-label" htmlFor="pl-header" style={{ marginTop: '0.75rem' }}>Header lines</label>
            <textarea id="pl-header" className="form-input" rows={5} value={form.headerLines} maxLength={2000} disabled={disabled}
              placeholder={profile.defaultHeaderLines} onChange={(e) => set('headerLines', e.target.value)} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              One line each: address, phones, GSTIN, FSSAI… Leave empty to print the property&apos;s details (shown greyed).
            </div>

            <div className="form-grid-2" style={{ marginTop: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="pl-layout">Layout</label>
                <select id="pl-layout" className="form-input" value={form.layoutCode} disabled={disabled}
                  onChange={(e) => set('layoutCode', e.target.value)}>
                  {profile.layouts.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pl-font">Font size</label>
                <select id="pl-font" className="form-input" value={form.fontScale} disabled={disabled}
                  onChange={(e) => set('fontScale', Number(e.target.value))}>
                  {FONT_SCALES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  {!FONT_SCALES.some((s) => s.value === form.fontScale) && <option value={form.fontScale}>{form.fontScale}×</option>}
                </select>
              </div>
            </div>
          </section>

          {/* ---- body ---- */}
          <section className="card" style={{ padding: '1rem', minWidth: 0 }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Optional boxes</h3>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              The guest&apos;s name and address, stay dates, persons, mobile, nationality, ID, the foreign-national block,
              signatures, rooms and plan always print.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '0.3rem 0.4rem', width: 60 }}>Print</th>
                    <th style={{ padding: '0.3rem 0.4rem' }}>Label on the card</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.optionalFields.map((f) => {
                    const shown = !form.hidden.includes(f.code);
                    return (
                      <tr key={f.code} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input type="checkbox" id={`pl-show-${f.code}`} checked={shown} disabled={disabled}
                            onChange={(e) => toggleField(f.code, e.target.checked)} aria-label={`Print ${f.label}`} />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input id={`pl-label-${f.code}`} className="form-input" style={{ height: '1.9rem', fontSize: '0.8rem' }}
                            value={form.labels[f.code] ?? ''} maxLength={40} disabled={disabled || !shown}
                            placeholder={f.label} onChange={(e) => setLabel(f.code, e.target.value)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <label className="form-label" htmlFor="pl-terms" style={{ marginTop: '0.75rem' }}>Terms</label>
            <textarea id="pl-terms" className="form-input" rows={5} value={form.termsText} maxLength={4000} disabled={disabled}
              placeholder={profile.defaultTermsText} onChange={(e) => set('termsText', e.target.value)} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>One declaration per line; they print numbered. Leave empty for the wording shown greyed.</div>

            <label className="form-label" htmlFor="pl-footer" style={{ marginTop: '0.75rem' }}>Footer</label>
            <input id="pl-footer" className="form-input" value={form.footerText} maxLength={500} disabled={disabled}
              placeholder="e.g. Thank you for staying with us" onChange={(e) => set('footerText', e.target.value)} />
          </section>

          {canEdit && (
            <section className="card" style={{ padding: '1rem', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>Copy from another property</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Takes its layout, logo, terms, footer, font size and optional boxes. This property keeps its own name and header lines.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 240, flex: 1 }}>
                  <SyutSelect
                    label="From"
                    isAsync
                    loadOptions={loadPropertyOptions}
                    defaultOptions
                    cacheOptions={false}
                    value={copyFrom}
                    onChange={(v) => setCopyFrom((v as string) || '')}
                    placeholder="Select property..."
                    size="sm"
                    menuPortalTarget={document.body}
                  />
                </div>
                <button className="btn btn-secondary btn-sm" disabled={busy || !copyFrom || copyFrom === propertyId}
                  onClick={() => { void copy(); }}>Copy</button>
              </div>
            </section>
          )}
        </div>
      )}
    </EnterpriseScreenShell>
  );
};

export default PrintLayoutsScreen;
